import { queryOptions, useQuery } from '@tanstack/react-query';

import { ApiResponse, QueryOptions } from '@cast/shared-types';
import { ObjectFilterV2 } from '@cast/types-patching-engine';
import { makeIsEnabled } from '@cast/utils';

import { apiClient } from '@cast/infrastructure-api';
import { useOrganizations } from '@cast/shared-organization-context-data-access';

const POD_MUTATIONS_PREVIEW_MATCHED_WORKLOADS_QUERY_KEY =
  'autoscaler-pod-mutations-preview-matched-workloads';

const apiFn = apiClient.autoscaler.previewMatchedWorkloads;

type PreviewMatchedWorkloadsQueryData = ApiResponse<typeof apiFn>;

const getPreviewMatchedWorkloadsQueryKey = ({
  clusterId,
  organizationId,
  filterString,
  name,
}: {
  clusterId?: string;
  organizationId?: string;
  filterString?: string;
  name?: string;
}) => {
  const key = [POD_MUTATIONS_PREVIEW_MATCHED_WORKLOADS_QUERY_KEY];
  if (clusterId !== undefined) key.push(clusterId);
  if (organizationId !== undefined) key.push(organizationId);
  if (filterString !== undefined) key.push(filterString);
  if (name !== undefined) key.push(name);
  return key;
};

type PreviewMatchedWorkloadsQueryArgs<SELECT> = QueryOptions<
  PreviewMatchedWorkloadsQueryData,
  SELECT
> & {
  clusterId?: string;
  filter?: ObjectFilterV2;
  name?: string;
};

const getPreviewMatchedWorkloadsQueryOptions = ({
  clusterId,
  organizationId,
  filterString,
  isFilterEmpty,
  filter,
  name,
  enabled,
}: {
  clusterId?: string;
  organizationId?: string;
  filterString: string;
  isFilterEmpty: boolean;
  filter?: ObjectFilterV2;
  name?: string;
  enabled?: PreviewMatchedWorkloadsQueryArgs<unknown>['enabled'];
}) =>
  queryOptions({
    queryKey: getPreviewMatchedWorkloadsQueryKey({
      clusterId,
      organizationId,
      filterString,
      name,
    }),
    queryFn: async () => {
      const { data } = await apiFn({
        organizationId: organizationId!,
        clusterId: clusterId!,
        previewMatchedWorkloadsRequest: {
          name,
          clusterId: clusterId!,
          organizationId: organizationId!,
          objectFilterV2: filter,
        },
      });
      return data;
    },
    enabled: makeIsEnabled(
      !!clusterId,
      !!organizationId,
      !!name,
      !isFilterEmpty,
      enabled
    ),
  });

export const usePreviewMatchedWorkloadsQuery = <
  SELECT = PreviewMatchedWorkloadsQueryData
>({
  clusterId,
  filter,
  name,
  enabled,
  select,
}: PreviewMatchedWorkloadsQueryArgs<SELECT>) => {
  const { currentOrganization } = useOrganizations();

  const filterString = JSON.stringify(filter);
  const isFilterEmpty = !filterString || filterString === '{}';

  return useQuery({
    ...getPreviewMatchedWorkloadsQueryOptions({
      clusterId,
      organizationId: currentOrganization?.id,
      filterString,
      isFilterEmpty,
      filter,
      name,
      enabled,
    }),
    select,
  });
};
